import { useState, useEffect, useCallback } from "react";
import { graphqlClient } from "@/services/core/graphqlClient";

export interface FacilityAvailability {
  facility_availability_id: number;
  clinic_facility_id: number;
  available_from_date: string;
  available_to_date: string;
  is_active: boolean;
  is_deleted: boolean;
  created_by: number;
  updated_by: number | null;
  created_datetime: string;
  updated_datetime: string | null;
  clinic_name: string;
  facility_name: string;
}

interface FacilityAvailabilityResponse {
  ref_facility_availability: Array<{
    is_active: boolean;
    is_deleted: boolean;
    available_from_date: string;
    available_to_date: string;
    clinic_facility_id: number;
    created_by: number;
    facility_availability_id: number;
    updated_by: number | null;
    created_datetime: string;
    updated_datetime: string | null;
    available_facility: Array<{
      facility_name: string;
      clinic_details: {
        name: string;
        id: number;
      };
    }>;
  }>;
  ref_facility_availability_aggregate: {
    aggregate: {
      count: number;
    };
  };
}

const GET_FACILITY_AVAILABILITY = `
  query GetRefFacilityAvailability($limit: Int, $offset: Int, $where: ref_facility_availability_bool_exp) {
    ref_facility_availability(
      where: $where
      limit: $limit
      offset: $offset
      order_by: {created_datetime: desc}
    ) {
      is_active
      is_deleted
      available_from_date
      available_to_date
      clinic_facility_id
      created_by
      facility_availability_id
      updated_by
      created_datetime
      updated_datetime
      available_facility {
        facility_name
        clinic_details {
          name
          id
        }
      }
    }
    ref_facility_availability_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const useFacilityAvailability = () => {
  const [facilityAvailabilities, setFacilityAvailabilities] = useState<FacilityAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter states
  const [filterType, setFilterType] = useState<string>("clinic_name");
  const [searchValue, setSearchValue] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState<Date | undefined>();
  const [toDateFilter, setToDateFilter] = useState<Date | undefined>();

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  const buildWhereClause = useCallback(() => {
    const baseWhere: any = {
      is_active: { _eq: true },
      is_deleted: { _eq: false }
    };

    if (searchValue.trim()) {
      if (filterType === "clinic_name") {
        baseWhere.available_facility = {
          clinic_details: {
            name: { _ilike: `%${searchValue.trim()}%` }
          }
        };
      } else if (filterType === "facility_name") {
        baseWhere.available_facility = {
          facility_name: { _ilike: `%${searchValue.trim()}%` }
        };
      }
    }

    if (fromDateFilter) {
      const fromDateStr = fromDateFilter.toISOString().split('T')[0];
      baseWhere.available_from_date = { _gte: fromDateStr };
    }

    if (toDateFilter) {
      const toDateStr = toDateFilter.toISOString().split('T')[0];
      baseWhere.available_to_date = { _lte: toDateStr };
    }

    return baseWhere;
  }, [filterType, searchValue, fromDateFilter, toDateFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use hasuraToken for GraphQL queries (required for Hasura JWT claims)
      const hasuraToken = localStorage.getItem("hasuraToken") || "";
      const offset = (currentPage - 1) * entriesPerPage;
      const whereClause = buildWhereClause();

      const response = await graphqlClient.query<FacilityAvailabilityResponse>(
        GET_FACILITY_AVAILABILITY,
        {
          limit: entriesPerPage,
          offset,
          where: whereClause
        },
        hasuraToken,
        "GetRefFacilityAvailability"
      );

      if (response.success && response.data) {
        const mappedData = response.data.ref_facility_availability.map((item) => ({
          facility_availability_id: item.facility_availability_id,
          clinic_facility_id: item.clinic_facility_id,
          available_from_date: item.available_from_date,
          available_to_date: item.available_to_date,
          is_active: item.is_active,
          is_deleted: item.is_deleted,
          created_by: item.created_by,
          updated_by: item.updated_by,
          created_datetime: item.created_datetime,
          updated_datetime: item.updated_datetime,
          clinic_name: item.available_facility?.[0]?.clinic_details?.name || "N/A",
          facility_name: item.available_facility?.[0]?.facility_name || "N/A"
        }));

        setFacilityAvailabilities(mappedData);
        setTotalCount(response.data.ref_facility_availability_aggregate.aggregate.count);
      } else {
        setError("Failed to fetch facility availability data");
      }
    } catch (err: any) {
      console.error("Error fetching facility availability:", err);
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, buildWhereClause]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  const clearSearch = () => {
    setSearchValue("");
    setFromDateFilter(undefined);
    setToDateFilter(undefined);
    setCurrentPage(1);
  };

  const refetch = () => {
    fetchData();
  };

  return {
    facilityAvailabilities,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    fromDateFilter,
    toDateFilter,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    setFromDateFilter,
    setToDateFilter,
    handleSearch,
    clearSearch,
    refetch,
  };
};
